import {
	LeadMagnetMedia,
	LeadMagnetResponsiveMedia,
	QuizAnswers,
	QuizQuestion,
	ResultInsight
} from '@/types/lead-magnet';

export type FailedSignal = QuizQuestion & { resultInsight: ResultInsight };

export const findAnswerOption = (question: QuizQuestion, value?: string) =>
	value ? (question.answers.find(answer => answer.value === value) ?? null) : null;

export const isQuestionAnswered = (question: QuizQuestion, answers: QuizAnswers) =>
	!!findAnswerOption(question, answers[question.slug]);

export const isQuestionPassed = (question: QuizQuestion, answers: QuizAnswers) =>
	findAnswerOption(question, answers[question.slug])?.isPass === true;

export const selectScore = (questions: QuizQuestion[], answers: QuizAnswers) =>
	questions.filter(question => isQuestionPassed(question, answers)).length;

// Unanswered questions are treated as failed; only signals with breakdown content are returned.
export const selectFailedQuestions = (
	questions: QuizQuestion[],
	answers: QuizAnswers
): FailedSignal[] =>
	questions.filter(
		(question): question is FailedSignal =>
			!isQuestionPassed(question, answers) && !!question.resultInsight
	);

export const selectAnsweredCount = (questions: QuizQuestion[], answers: QuizAnswers) =>
	questions.filter(question => isQuestionAnswered(question, answers)).length;

export const selectFirstUnansweredIndex = (questions: QuizQuestion[], answers: QuizAnswers) => {
	const index = questions.findIndex(question => !isQuestionAnswered(question, answers));

	return index === -1 ? questions.length : index;
};

export const buildCopyTokens = (questions: QuizQuestion[], answers: QuizAnswers) => {
	const total = questions.length;
	const passed = selectScore(questions, answers);

	return {
		score: passed,
		passed,
		failed: total - passed,
		total
	};
};

// Mobile media is optional, so desktop is the fallback.
export const resolveResponsiveMedia = (
	media: LeadMagnetResponsiveMedia | null,
	isMobile: boolean
): LeadMagnetMedia | null => {
	if (!media) return null;

	return isMobile ? (media.mobile ?? media.desktop) : media.desktop;
};
